import { SensorType, SENSOR_TYPE_LIST } from '@innovyze/shared-utils';
import {
	CreateValuemappingProfileRequest,
	GetSingleValuemappingProfileResponse,
	Mapping,
} from '@Types/valuemappingManager.types';
import { ValuemappingProfileFormShape } from '@Types/valuemappingForm.types';
import { TFunc } from '@Translations/types';

export type MappingMethod = 'Custom' | 'Last Known Value';
export type MappingMethodShort = 'CUSTOM' | 'LKV';
export const METHODS: Record<MappingMethod, MappingMethodShort> = {
	Custom: 'CUSTOM',
	'Last Known Value': 'LKV',
};
export const METHODS_REVERSE: Record<MappingMethodShort, MappingMethod> = {
	CUSTOM: 'Custom',
	LKV: 'Last Known Value',
};

function regenerateMappings(
	mappings: CreateValuemappingProfileRequest['mappings'],
): CreateValuemappingProfileRequest['mappings'] {
	const mappingsClone = Array.from(mappings);

	if (
		mappingsClone &&
		mappingsClone.length > 1 &&
		!mappingsClone[mappingsClone.length - 1].from
	) {
		mappingsClone.pop();
	}

	return mappings.map((mapping, idx) => {
		const shouldCleanTo =
			METHODS[mapping.method as MappingMethod] !== METHODS.Custom;

		return {
			...mapping,
			order: idx,
			from: mapping.from,
			to: shouldCleanTo ? '' : mapping.to,
			method: METHODS[mapping.method as MappingMethod],
		};
	});
}

export const createValuemappingProfileCreateRequest = (
	values: ValuemappingProfileFormShape,
): CreateValuemappingProfileRequest => {
	return {
		name: values.profileName,
		mappings: regenerateMappings(values.mappings),
		appliesTo: values.sensorTypes
			.map(sensorType => ({
				type: 'sensor_type',
				field: sensorType,
			}))
			.concat(
				values.sensorIds.map(sensorId => ({
					type: 'sensor_id',
					field: sensorId,
				})),
			),
	};
};

export const parseValuemappingProfileResponse = (
	values: GetSingleValuemappingProfileResponse,
): ValuemappingProfileFormShape => {
	return {
		profileName: values.name,
		sensorTypes: values.sensors
			.filter(sensor =>
				SENSOR_TYPE_LIST.includes(sensor.field as SensorType),
			)
			.map(cur => cur.field),
		sensorIds: values.sensors
			.filter(
				sensor =>
					!SENSOR_TYPE_LIST.includes(sensor.field as SensorType),
			)
			.map(cur => cur.field),
		mappings: values.mappings.map((mapping, idx) => ({
			...mapping,
			order: idx,
			method: METHODS_REVERSE[mapping.method as MappingMethodShort],
		})),
	};
};

export const generateInitialMappings = (mappings: Mapping[]): Mapping[] => {
	const baseInitialMapping = {
		from: null,
		method: 'Last Known Value',
		to: null,
	};

	const ret: Mapping[] = Array.from(mappings);

	if (ret.length === 0) {
		ret.push({ ...baseInitialMapping, order: 0 });
	}

	return ret;
};

export const TSensorTypes = (t: TFunc): { [key: string]: string } => ({
	Billing: t('Billing'),
	'Chlorine Residuals': t('Chlorine Residuals'),
	Current: t('Current'),
	'Flow Rate': t('Flow Rate'),
	Head: t('Head'),
	Level: t('Level'),
	Other: t('Other'),
	Power: t('Power'),
	Pressure: t('Pressure'),
	'Rain Gauge': t('Rain Gauge'),
	Setting: t('Setting'),
	Status: t('Status'),
	'Tank Level': t('Tank Level'),
	Temperature: t('Temperature'),
	Turbidity: t('Turbidity'),
	Velocity: t('Velocity'),
	Volume: t('Volume'),
	'No Units': t('No Units'),
	MultiType: t('Multi-Type'),
});
